import { useState, useEffect } from "react";
import axios from "axios";
import { HelpCircle, Search, ChevronDown, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";

import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQHub() {
  useSEO();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [faqsRes, catsRes] = await Promise.all([
          axios.get(`${API}/faqs`),
          axios.get(`${API}/faqs/categories`),
        ]);
        setFaqs(faqsRes.data);
        setCategories(catsRes.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to fetch FAQs:', err);
          }
        }
        // FAQ list will be empty on error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory =
      selectedCategory === "all" || faq.category === selectedCategory;
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryLabel = (id) => {
    const cat = categories.find((c) => c.id === id);
    return cat ? cat.name : id;
  };

  // Generate FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": filteredFaqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <div data-testid="faq-hub" className="">
      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <HelpCircle className="w-4 h-4 text-orange" />
              Knowledge Center
            </div>
            <h1
              data-testid="faq-title"
              className="font-heading text-4xl sm:text-5xl font-semibold text-white tracking-tight mb-6"
            >
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Find answers to common questions about storm damage, insurance claims,
              roofing costs, and choosing the right contractor.
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-gray-50 border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="faq-search"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white"
              />
            </div>
            <div className="text-gray-500 text-sm">
              {filteredFaqs.length} questions found
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading FAQs...</div>
          ) : (
            <Tabs
              defaultValue="all"
              value={selectedCategory}
              onValueChange={setSelectedCategory}
              className="w-full"
            >
              {/* Category Tabs */}
              <div className="mb-8 overflow-x-auto pb-2">
                <TabsList className="bg-gray-100 p-1 rounded-lg inline-flex min-w-full md:min-w-0">
                  <TabsTrigger
                    value="all"
                    data-testid="faq-tab-all"
                    className="px-6 py-2.5 rounded-md data-[state=active]:bg-white data-[state=active]:text-navy data-[state=active]:shadow-sm"
                  >
                    All ({faqs.length})
                  </TabsTrigger>
                  {categories.map((cat) => (
                    <TabsTrigger
                      key={cat.id}
                      value={cat.id}
                      data-testid={`faq-tab-${cat.id}`}
                      className="px-6 py-2.5 rounded-md whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-navy data-[state=active]:shadow-sm"
                    >
                      {cat.name} ({cat.count})
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* FAQ Accordion */}
              <TabsContent value={selectedCategory} className="mt-0">
                {filteredFaqs.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">
                      No questions found matching your search.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedCategory("all");
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="space-y-4">
                    {filteredFaqs.map((faq) => (
                      <AccordionItem
                        key={faq.id || faq.question}
                        value={`faq-${faq.id || faq.question}`}
                        data-testid={`faq-item-${faq.id}`}
                        className="border border-gray-200 rounded-lg px-6 bg-white hover:border-orange/30 transition-colors"
                      >
                        <AccordionTrigger className="text-left py-5 hover:no-underline">
                          <div className="flex items-start gap-4 pr-4">
                            <div className="w-8 h-8 bg-orange/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                              <HelpCircle className="w-4 h-4 text-orange" />
                            </div>
                            <div>
                              <span className="font-heading font-semibold text-navy text-lg block">
                                {faq.question}
                              </span>
                              {selectedCategory === "all" && (
                                <span className="text-gray-400 text-sm mt-1 block">
                                  {getCategoryLabel(faq.category)}
                                </span>
                              )}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-5 pl-12">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Connect with a local Minnesota storm damage expert who can answer your
            specific questions and help you understand your options.
          </p>
          <Link to="/#lead-form">
            <Button
              data-testid="faq-cta-btn"
              className="bg-orange hover:bg-orange-hover text-white px-8 py-4 text-lg rounded-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Get Connected
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
